import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Button, Modal, Popconfirm, Popover, Space, Tag} from "antd";
import dayjs from "dayjs";
import {PlayerContext} from "../../../shared/contexts/playerContext";
import {useNavigate} from "react-router-dom";
import TablePage from "../../../pages/tablePage/TablePage";
import {useItemKrTextData, usePackageDataTable, useShopDataTable} from "../../probability/useItemData";
import IsLoading from "../../../shared/components/IsLoading";
import ErrorPage from "../../../shared/components/ErrorPage";
import {getPaymentLogColumns} from "../../logs/columns/paymentLogColumns";
import {AuthContext} from "../../../shared/contexts/authContext";
import {internalApi} from "../../../common/axios";
import toast from "react-hot-toast";
import {buildUrl} from "../../../shared/utils/net";
import {PRODUCT_TYPE, SHOP_TYPE} from "../../../shared/utils/enum";

const ActionCell = ({ record, onClaimStatus, onResend, shopData }) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(null);
    const isModalOpen = useRef(false);

    const validPackage = useMemo(() => {
        const NO_NEED_ITEM = [PRODUCT_TYPE.CHAIN, PRODUCT_TYPE.ENERGY];
        const packageInfo = shopData[record.productId]?.data;
        return !(!packageInfo || (packageInfo.shopType !== SHOP_TYPE.PACKAGE && packageInfo.shopType !== SHOP_TYPE.DIAMOND) || NO_NEED_ITEM.includes(packageInfo.productType));

    },[record.productId, shopData]);


    const handleClaimStatus = async () => {
        setLoading('claim');
        setMessage('');
        try {
            const msg = await onClaimStatus(record.itemId, record.created);
            setMessage(msg ?? '');
        } catch {
            setMessage('오류가 발생했습니다.');
        } finally {
            setLoading(null);
        }
    };

    const handleResend = () => {
        isModalOpen.current = true;
        Modal.confirm({
            title: '재지급 확인',
            content: `상품을 재지급하시겠습니까?`,
            okText: '재지급',
            okType: 'danger',
            cancelText: '취소',
            afterClose: () => { isModalOpen.current = false; },
            onOk: async () => {
                setLoading('resend');
                setMessage('');
                try {
                    const msg = await onResend(record.productId, record.itemId);
                    setMessage(msg ?? '');
                } catch {
                    setMessage('오류가 발생했습니다.');
                } finally {
                    setLoading(null);
                }
            },
        });
    };

    const content = (
        <Space direction="vertical" size="small" style={{minWidth: 120}}>
            <Space>
                <Button size="small" loading={loading === 'claim'} onClick={handleClaimStatus}>수령체크</Button>
                <Button size="small" loading={loading === 'resend'} onClick={handleResend} type="primary" danger>재지급</Button>
            </Space>
            {message && <div style={{fontSize: 12}}>{message}</div>}
        </Space>
    );

    return (
        <Popover
            content={content}
            trigger="click"
            open={open}
            onOpenChange={(v) => { if (!v && isModalOpen.current) return; setOpen(v); if (!v) { setMessage(''); setLoading(null); } }}
        >
            <Button
                size="small"
                disabled={!validPackage}
            >
                ···
            </Button>
        </Popover>
    );
};

const Payment = () => {
    const {playerData} = useContext(PlayerContext);
    const {currentUser} = useContext(AuthContext);

    const {data: krItemTextData, isLoading: isKrItemTextDataLoading, error: krItemTextDataError} = useItemKrTextData();
    const {data: packageData, isLoading: isPackageDataLoading, error: packageDataError} = usePackageDataTable();
    const {data: shopData, isLoading: isShopDataLoading, error: shopDataError} = useShopDataTable();

    const refreshRef = useRef(null);

    const navigate = useNavigate();

    useEffect(() => {
        if (!playerData.userKey) {
            navigate('/player');
        }
    }, [playerData, navigate]);

    if (isKrItemTextDataLoading || isPackageDataLoading || isShopDataLoading) return <IsLoading/>;
    const error = krItemTextDataError || packageDataError || shopDataError;
    if (error) return <ErrorPage error={error}/>;

    const onClaimStatus = async (itemId, created) => {
        const {data} = await internalApi.get(buildUrl('player/payment/claimed', {
            userKey: playerData.userKey,
            itemId,
            created
        }))

        switch (data.mailStatus) {
            case 0:
                return <Tag color="default">상품 지급 안됨</Tag>;
            case 1:
                return (
                    <Space direction="vertical" size={2}>
                        <Tag color="blue">수령 전</Tag>
                        <span>발송일자: {dayjs(data.created).format('YYYY-MM-DD HH:mm:ss')}</span>
                    </Space>
                );
            case 2:
                return (
                    <Space direction="vertical" size={2}>
                        <Tag color="green">수령 완료</Tag>
                        <span>수신일자: {dayjs(data.deleted).format('YYYY-MM-DD HH:mm:ss')}</span>
                    </Space>
                );
            default:
                return <Tag color="red">에러</Tag>;
        }
    }

    const onResend = async (productId) => {
        const res = await internalApi.post('player/payment/resend', {userKey: playerData.userKey, productId});
        return res.data.message;
    }

    const onDelete = async (orderId) => {
        try {
            const res = await internalApi.delete('player/payment', {data: {userKey: playerData.userKey, orderId}});
            toast.success(res.data.message);
            refreshRef.current?.();
        } catch {
            // 에러 메시지는 axios 인터셉터가 표시
        }
    }

    const columns = getPaymentLogColumns(packageData, krItemTextData[currentUser.target], {onClaimStatus, onResend});
    columns.push(        {
        title: '',
        key: 'action',
        render: (_, record) => (
            <ActionCell record={record} onClaimStatus={onClaimStatus} onResend={onResend} shopData={shopData[currentUser.target]} />
        ),
    })
    columns.push({
        title: '',
        key: 'delete',
        width: 70,
        render: (_, record) => (
            <Popconfirm
                title="결제 로그 삭제"
                description={`영수증 ${record.orderId} 로그를 삭제합니다. 되돌릴 수 없습니다.`}
                okText="삭제"
                okType="danger"
                cancelText="취소"
                onConfirm={() => onDelete(record.orderId)}
            >
                <Button size="small" danger>삭제</Button>
            </Popconfirm>
        ),
    })

    const props = {
        columns,
        refreshRef,
        url: `player/payment?userKey=${playerData.userKey}`,
    }

    return (
            <TablePage {...props}/>
    );
};

export default Payment;